import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { api } from "../../../lib/api.js";
import { logError } from "../../../lib/logger.js";
import { CardTitle } from "../../ui/card.js";
import { GlowCard } from "../../ui/glow-card.js";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../../ui/table.js";
import type { WidgetProps } from "../widget-registry.js";
import { PendingLeaveSkeleton } from "./widget-skeletons.js";

interface LeaveRequest {
  id: string; status: string; leaveType: string; startDate: string; endDate: string; days: number;
  employee: { firstName: string; lastName: string; employeeCode: string };
}

/** The five oldest leave requests awaiting a decision. */
export function PendingLeaveWidget(_props: WidgetProps) {
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get<{ data: LeaveRequest[] }>("/leave/requests?status=PENDING&limit=5")
      .then((res) => { if (!cancelled) setRequests(res.data.data ?? []); })
      .catch((e: unknown) => { if (!cancelled) logError("PendingLeaveWidget.load", e); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    // glow="ultra" is the lightest touch above off: the edge nearest the cursor
    // lifts just enough to show the card reacts, with no bloom past the border.
    // spotlight="low" matches it: the interior pool defaults to medium, which
    // reads as a bright wash on a sparse card like Quick Actions.
    <GlowCard glow="ultra" spotlight="low" size="lg" className="h-full">
      {loading ? <PendingLeaveSkeleton /> : (<>
      <CardTitle className="mb-4">Pending Leave Requests</CardTitle>
      {requests.length === 0 ? (
        <p className="text-[13px] text-surface-400">No pending requests.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              {["Employee", "Type", "Days", "From"].map((h) => <TableHead key={h}>{h}</TableHead>)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {requests.map((r) => (
              <TableRow key={r.id}>
                <TableCell>
                  {r.employee.firstName} {r.employee.lastName}
                  <br />
                  <span className="text-[11px] text-surface-400">{r.employee.employeeCode}</span>
                </TableCell>
                <TableCell>{r.leaveType}</TableCell>
                <TableCell>{r.days}</TableCell>
                <TableCell>{new Date(r.startDate).toLocaleDateString()}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Link to="/leave" className="mt-3 block text-xs text-brand-600 no-underline">
        View all leave requests →
      </Link>
      </>)}
    </GlowCard>
  );
}
